import { Packshot } from './packshot';
/**
 * Runner Boy brand. The mark is a rounded-square "R" whose leg resolves into a forward motion arrow with three
 * marigold speed strokes — urban, quick, dependable. Deliberately no mascot, no bottles, nothing aimed at minors.
 */
export function LogoMark({ size = 36, title = 'Runner Boy', inverted = false }: { size?: number; title?: string; inverted?: boolean }) {
  // Midnight tile with a soft top light, ivory "R", signal-amber speed strokes. Inverted = ivory tile for dark surfaces.
  const bg = inverted ? '#f7f4ee' : '#0e1324';
  const fg = inverted ? '#0e1324' : '#f7f4ee';
  const gid = `rbm-${size}-${inverted ? 'i' : 'n'}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" role="img" aria-label={title}>
      <title>{title}</title>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity={inverted ? 0 : 0.14} />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="60" height="60" rx="17" fill={bg} />
      <rect x="2" y="2" width="60" height="30" rx="17" fill={`url(#${gid})`} />
      <path d="M22 46V18h13.5c6.4 0 10.5 3.6 10.5 9.1 0 4.1-2.3 7.1-6.1 8.3L47.5 46h-7.8l-6.6-9.9H29V46h-7Zm7-15.7h6c2.7 0 4.3-1.4 4.3-3.6 0-2.3-1.6-3.6-4.3-3.6h-6v7.2Z" fill={fg} />
      <g stroke="#ffaa1f" strokeWidth="3.2" strokeLinecap="round">
        <path d="M9.5 28h7" />
        <path d="M7 34h9.5" />
        <path d="M9.5 40h7" />
      </g>
    </svg>
  );
}

export function Wordmark({ className = '', inverted = false, size = 'md' }: { className?: string; inverted?: boolean; size?: 'sm' | 'md' | 'lg' }) {
  const text = size === 'lg' ? 'text-[30px]' : size === 'sm' ? 'text-[19px]' : 'text-[22px]';
  const mark = size === 'lg' ? 44 : size === 'sm' ? 28 : 34;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <LogoMark size={mark} inverted={inverted} />
      <span className={`rb-display ${text} font-bold leading-none tracking-[-0.03em] ${inverted ? 'text-white' : 'text-ink-900'}`}>
        Runner<span className={inverted ? 'text-marigold-500' : 'text-peacock-500'}>Boy</span>
      </span>
    </span>
  );
}

/** Generic, brand-free category artwork used as safe placeholders until retailers upload authorised imagery. */
const CATEGORY_COLORS: Record<string, [string, string]> = {
  beer: ['#f2a541', '#fdeed8'],
  whisky: ['#b46a0c', '#f9d7a6'],
  rum: ['#7a3b16', '#f1d9c6'],
  vodka: ['#5b6573', '#e7eaee'],
  gin: ['#12737a', '#d7eeef'],
  wine: ['#7c2341', '#f4d9e2'],
  tequila: ['#a3a314', '#f3f3cf'],
  brandy: ['#8a4b19', '#f0dcc8'],
  liqueur: ['#6d3f8f', '#eadff3'],
  rtd: ['#d64545', '#fdeeee'],
  'non-alcoholic': ['#1f9d55', '#e9f7ef'],
  water: ['#2f6fde', '#eaf1fd'],
  soda: ['#2c8c93', '#eef8f8'],
  tonic: ['#2c8c93', '#eef8f8'],
  mixers: ['#dd8b1f', '#fff8ee'],
  'soft-drinks': ['#b83535', '#fdeeee'],
  juices: ['#f2a541', '#fff8ee'],
  'energy-drinks': ['#2459b8', '#eaf1fd'],
  snacks: ['#dd8b1f', '#fdeed8'],
  namkeen: ['#b46a0c', '#fdeed8'],
  nuts: ['#8a4b19', '#f0dcc8'],
  ice: ['#7fb8d8', '#eef6fb'],
  'cups-essentials': ['#5b6573', '#f4f5f7'],
};

export function CategoryArt({ slug, className = '' }: { slug: string; className?: string }) {
  const [fg, bg] = CATEGORY_COLORS[slug] ?? ['#5b6573', '#f4f5f7'];
  const bottle = ['whisky', 'rum', 'vodka', 'gin', 'wine', 'tequila', 'brandy', 'liqueur', 'water', 'soda', 'tonic', 'juices', 'soft-drinks'].includes(slug);
  const can = ['beer', 'rtd', 'mixers', 'energy-drinks', 'non-alcoholic'].includes(slug);
  return (
    <svg viewBox="0 0 120 120" className={className} role="img" aria-label={`${slug} product image placeholder`}>
      <rect width="120" height="120" rx="18" fill={bg} />
      {bottle && (
        <g fill={fg}>
          <rect x="53" y="18" width="14" height="18" rx="3" />
          <path d="M48 38h24c3 6 6 10 6 18v40a6 6 0 0 1-6 6H48a6 6 0 0 1-6-6V56c0-8 3-12 6-18Z" opacity="0.92" />
          <rect x="47" y="62" width="26" height="18" rx="3" fill="#fff" opacity="0.85" />
        </g>
      )}
      {can && (
        <g fill={fg}>
          <rect x="40" y="28" width="40" height="66" rx="8" opacity="0.92" />
          <rect x="44" y="24" width="32" height="6" rx="3" />
          <rect x="44" y="50" width="32" height="20" rx="3" fill="#fff" opacity="0.85" />
        </g>
      )}
      {!bottle && !can && slug !== 'ice' && (
        <g fill={fg}>
          <path d="M34 34h52l-6 58H40Z" opacity="0.9" />
          <rect x="30" y="28" width="60" height="9" rx="4" />
          <rect x="44" y="52" width="32" height="16" rx="3" fill="#fff" opacity="0.85" />
        </g>
      )}
      {slug === 'ice' && (
        <g fill={fg} opacity="0.85">
          <rect x="32" y="40" width="24" height="24" rx="5" transform="rotate(-10 44 52)" />
          <rect x="60" y="34" width="24" height="24" rx="5" transform="rotate(12 72 46)" />
          <rect x="48" y="62" width="24" height="24" rx="5" transform="rotate(4 60 74)" />
        </g>
      )}
    </svg>
  );
}

export function ProductImage({
  url,
  categorySlug,
  alt,
  className = '',
  brand,
  volumeMl = null,
}: {
  url: string | null;
  categorySlug: string;
  alt: string;
  className?: string;
  /** When given, the fallback is a generated packshot labelled with the brand and size instead of a category icon. */
  brand?: string;
  volumeMl?: number | null;
}) {
  if (!url || url.startsWith('/placeholders/'))
    return brand !== undefined ? <Packshot brand={brand} name={alt} categorySlug={categorySlug} volumeMl={volumeMl} className={className} /> : <CategoryArt slug={categorySlug} className={className} />;
  return <img src={url} alt={alt} loading="lazy" decoding="async" className={`object-contain ${className}`} />;
}
